/**
 * Camp outreach opt-out (KB-5226).
 *
 * A camp's "do not contact" flag lives on its primary contact,
 * `campOrganizations/{orgId}.contacts[0]`: `doNotContact`, `doNotContactAt`
 * (a Timestamp) and `doNotContactSource` (`"link"` when the director clicked
 * an outreach unsubscribe link, `"staff"` when an admin set it here).
 *
 * The link carries a `kind: "camp_outreach"` token in the shared unsubscribe
 * registry (`system/automation/digestUnsubTokens`, KB-1078). kb-ff's
 * `digestUnsubscribeByToken` callable and `/unsubscribe` page act on it, and
 * the token can set this one flag on this one camp and nothing else. The
 * token is random, never derived from the org id, so a link can't be guessed
 * from a camp. It is never stored on the org doc, which is publicly readable.
 */
import {
  Timestamp,
  collection,
  doc,
  getDocs,
  limit,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  where,
  type Firestore,
} from "firebase/firestore";

export const TOKEN_REGISTRY_PATH = "system/automation/digestUnsubTokens";
export const CAMP_OUTREACH_KIND = "camp_outreach";

export type DoNotContactSource = "link" | "staff";

export type OrgContact = {
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  phone?: string;
  notes?: string;
  doNotContact?: boolean;
  doNotContactAt?: Timestamp;
  doNotContactSource?: DoNotContactSource;
  [key: string]: unknown;
};

const DO_NOT_CONTACT_KEYS = ["doNotContact", "doNotContactAt", "doNotContactSource"] as const;

const PAGE_BASE_BY_PROJECT: Record<string, string> = {
  "kb-prod-fb-proj": "https://kidsbooked.com/unsubscribe",
  "kb-staging-proj": "https://www-staging.kidsbooked.com/unsubscribe",
};

/** The /unsubscribe page for the Firebase project the token lives in. Unknown projects get prod. */
export function unsubscribePageBase(projectId: string | undefined): string {
  return (projectId && PAGE_BASE_BY_PROJECT[projectId]) || PAGE_BASE_BY_PROJECT["kb-prod-fb-proj"];
}

export function buildOutreachUnsubscribeUrl(token: string, projectId: string | undefined): string {
  return `${unsubscribePageBase(projectId)}?token=${encodeURIComponent(token)}`;
}

export function generateOutreachToken(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

/** contacts[0]'s do-not-contact state, or null when it isn't set. */
export function primaryContactDoNotContact(
  contacts: unknown,
): { at: Timestamp | null; source: DoNotContactSource | null } | null {
  const primary = Array.isArray(contacts) && isPlainObject(contacts[0]) ? contacts[0] : null;
  if (!primary || primary.doNotContact !== true) return null;
  const at = primary.doNotContactAt instanceof Timestamp ? primary.doNotContactAt : null;
  const source =
    primary.doNotContactSource === "link" || primary.doNotContactSource === "staff"
      ? primary.doNotContactSource
      : null;
  return { at, source };
}

/**
 * The contacts array an org-form save should write: the form's fields over
 * the CURRENT contacts[0], so its do-not-contact flag and any other field the
 * form does not edit survive, and contacts[1..] stay as they are.
 */
export function withPrimaryContactFields(
  currentContacts: unknown,
  formContact: Pick<OrgContact, "email" | "firstName" | "lastName" | "phone" | "role" | "notes">,
): OrgContact[] {
  const list = Array.isArray(currentContacts) ? currentContacts : [];
  const primary = isPlainObject(list[0]) ? list[0] : {};
  return [{ ...primary, ...formContact }, ...list.slice(1)] as OrgContact[];
}

/**
 * `nextContacts` with contacts[0]'s do-not-contact fields replaced by the CURRENT contacts[0]'s, or
 * removed when the current one has none. For a write that rebuilds contacts from another source (a
 * history restore), so it can never set, clear or back-date the flag. Pure; the caller writes it.
 */
export function withCurrentDoNotContact(nextContacts: unknown[], currentContacts: unknown): unknown[] {
  const current = Array.isArray(currentContacts) && isPlainObject(currentContacts[0]) ? currentContacts[0] : {};
  const carried = DO_NOT_CONTACT_KEYS.filter((key) => current[key] !== undefined);
  if (carried.length === 0 && !isPlainObject(nextContacts[0])) return [...nextContacts];
  const primary: Record<string, unknown> = isPlainObject(nextContacts[0]) ? { ...nextContacts[0] } : {};
  for (const key of DO_NOT_CONTACT_KEYS) delete primary[key];
  for (const key of carried) primary[key] = current[key];
  return [primary, ...nextContacts.slice(1)];
}

/** contacts with contacts[0]'s flag set (staff) or cleared. Pure; the caller writes it. */
export function withDoNotContact(
  currentContacts: unknown,
  on: boolean,
  at: Timestamp,
): OrgContact[] {
  const list = Array.isArray(currentContacts) ? currentContacts : [];
  const primary: Record<string, unknown> = isPlainObject(list[0]) ? { ...list[0] } : {};
  if (on) {
    primary.doNotContact = true;
    primary.doNotContactAt = at;
    primary.doNotContactSource = "staff";
  } else {
    for (const key of DO_NOT_CONTACT_KEYS) delete primary[key];
  }
  return [primary, ...list.slice(1)] as OrgContact[];
}

/**
 * Admin action: set or clear the camp's do-not-contact flag. Reads and writes
 * in one transaction so a director's click that lands meanwhile is not lost
 * to a stale contacts array. Setting an already-set flag keeps its original
 * time and source.
 */
export async function setOrgDoNotContact(db: Firestore, orgId: string, on: boolean): Promise<void> {
  const orgRef = doc(db, "campOrganizations", orgId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(orgRef);
    if (!snap.exists()) throw new Error(`campOrganizations/${orgId} does not exist`);
    const contacts = (snap.data() as { contacts?: unknown }).contacts;
    const already = primaryContactDoNotContact(contacts) != null;
    if (already === on) return;
    tx.update(orgRef, { contacts: withDoNotContact(contacts, on, Timestamp.now()) });
  });
}

/**
 * The outreach unsubscribe URL for a camp, for KB-5197's campaign sends.
 * Reuses the camp's existing camp_outreach token, or mints one. Two
 * concurrent first calls can mint two tokens for one camp; both work, and
 * either sets the same flag.
 */
export async function getOutreachUnsubscribeUrl(
  db: Firestore,
  orgId: string,
  opts: { uid: string; projectId?: string; mint?: () => string },
): Promise<string> {
  const token = await getOutreachUnsubscribeToken(db, orgId, opts);
  return buildOutreachUnsubscribeUrl(token, opts.projectId ?? db.app.options.projectId);
}

/**
 * The raw camp_outreach token, for a sender that also needs the List-Unsubscribe
 * one-click URL (pro `buildUnsubscribeSpec(token)` builds that from the token).
 * Same reuse-or-mint rule as getOutreachUnsubscribeUrl.
 */
export async function getOutreachUnsubscribeToken(
  db: Firestore,
  orgId: string,
  opts: { uid: string; mint?: () => string },
): Promise<string> {
  const id = String(orgId || "").trim();
  if (!id || id.includes("/")) throw new Error("getOutreachUnsubscribeToken: invalid orgId");

  const existing = await getDocs(
    query(
      collection(db, TOKEN_REGISTRY_PATH),
      where("kind", "==", CAMP_OUTREACH_KIND),
      where("orgId", "==", id),
      limit(1),
    ),
  );
  if (!existing.empty) return existing.docs[0].id;

  const token = (opts.mint ?? generateOutreachToken)();
  await setDoc(doc(db, TOKEN_REGISTRY_PATH, token), {
    kind: CAMP_OUTREACH_KIND,
    orgId: id,
    status: "active",
    createdAt: serverTimestamp(),
    createdBy: opts.uid,
  });
  return token;
}
